/*
 * Video tutorials: client-side topic filtering and watched state.
 *
 * Nothing here is load-bearing. The pills are real links to Shopify's own
 * `/tagged/<tag>` route, so with this file blocked the page still filters — it
 * just does a round trip. What the component adds is the same filter without
 * one, plus the "Showing X of Y" counter and the watched badge, both of which
 * are meaningless without script and are hidden by CSS until it runs.
 *
 * Storage follows the theme's existing convention (`assets/wishlist.js`): one
 * namespaced key holding a JSON array, every access in try/catch because a
 * private window, cleared site data or a browser set to block storage all throw
 * rather than return empty. The brief asked for a key per article
 * (`watched:<handle>`); one namespaced key is the same information without
 * scattering entries across a shared origin, and it touches nothing else.
 */
(function () {
  const STORAGE_KEY = 'yarn-social:watched-tutorials';

  function readWatched() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!Array.isArray(raw)) return new Set();
      return new Set(raw.map(String).filter(Boolean));
    } catch (error) {
      return new Set();
    }
  }

  class VideoTutorialFilters extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-vt-grid]');
      if (!this.grid) return;

      this.cards = Array.from(this.querySelectorAll('[data-vt-card]'));
      this.chips = Array.from(this.querySelectorAll('[data-vt-tag]'));
      this.showingEl = this.querySelector('[data-vt-showing]');

      this.markWatched();

      /* The active tag comes from the URL the page was served with, so a
         bookmarked or shared `/tagged/x` link and a `?tag=x` pushed by this
         component both land on the same state. Server-filtered pages already
         hold the right cards, and filtering them again is a no-op. */
      this.apply(this.tagFromLocation(), { initial: true });

      this.addEventListener('click', (event) => this.onClick(event));
      /* Not `initial`: going back or forward changes which cards are showing,
         so the sentence has to change with them. */
      window.addEventListener('popstate', () => this.apply(this.tagFromLocation()));
    }

    tagFromLocation() {
      const fromQuery = new URLSearchParams(window.location.search).get('tag');
      if (fromQuery) return fromQuery;

      const match = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }

    markWatched() {
      const watched = readWatched();
      if (watched.size === 0) return;

      /* `article.handle` measured as "news/yarn-quantity-guidance" — the blog
         handle is part of it. That is fine as a key because the same attribute
         is what gets written back, but it is not the bare handle the brief
         assumed, so do not build these keys anywhere else by hand. */
      this.cards.forEach((card) => {
        if (watched.has(card.dataset.vtHandle)) card.dataset.watched = 'true';
      });
    }

    onClick(event) {
      const chip = event.target.closest('[data-vt-tag]');
      if (!chip || !this.contains(chip)) return;

      /* Leave modified clicks alone — a middle click or cmd-click means "open
         this somewhere else", and the href is a real page. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      const tag = chip.dataset.vtTag || '';

      /* A server-filtered page holds only that tag's cards, so switching to a
         different tag in place would filter an already-narrowed set and show
         nothing. Let the browser follow the link instead. */
      if (window.location.pathname.indexOf('/tagged/') !== -1) return;

      event.preventDefault();
      this.apply(tag);

      const url = new URL(window.location.href);
      if (tag) {
        url.searchParams.set('tag', tag);
      } else {
        url.searchParams.delete('tag');
      }
      window.history.pushState({ tag: tag }, '', url);
    }

    apply(tag, options) {
      const wanted = (tag || '').toLowerCase();
      let shown = 0;

      this.cards.forEach((card) => {
        const tags = (card.dataset.vtTags || '').split(',').filter(Boolean);
        const match = !wanted || tags.indexOf(wanted) !== -1;
        card.hidden = !match;
        if (match) shown += 1;
      });

      this.chips.forEach((chip) => {
        const isActive = (chip.dataset.vtTag || '').toLowerCase() === wanted;
        if (isActive) {
          chip.setAttribute('aria-current', 'true');
        } else {
          chip.removeAttribute('aria-current');
        }
      });

      /* Liquid already wrote a true sentence, so only the first pass leaves it
         be: rewriting a live region with the words it already holds is an
         announcement about nothing. Every later pass — a chip, or the back
         button — has actually changed what is on screen. */
      if (!(options && options.initial)) this.updateShowing(shown);
    }

    updateShowing(shown) {
      if (!this.showingEl) return;

      /* Two sentences, because "showing all of them" and "showing some of them"
         are different statements and only one of them wants two numbers. The
         choice is `shown === total` rather than "is a tag active", so a topic
         that happens to cover everything is described honestly too.

         Liquid hands both over with `%SHOWN%` and `%TOTAL%` standing in for the
         numbers, so the wording — and its word order, which differs in Turkish —
         stays in the locale files rather than being assembled here where it
         could not be translated. */
      const total = this.cards.length;
      /* The "all" sentence arrives finished: Liquid pluralised it — "the only
         tutorial" against "all 9 tutorials" — on the very number the script
         would substitute, so there is nothing left to fill in. Only the
         filtered sentence carries placeholders. */
      const template = shown === total ? this.dataset.labelShowingAll : this.dataset.labelShowing;
      if (!template) return;

      this.showingEl.textContent = template
        .replace('%SHOWN%', String(shown))
        .replace('%TOTAL%', String(total));
    }
  }

  if (!customElements.get('video-tutorial-filters')) {
    customElements.define('video-tutorial-filters', VideoTutorialFilters);
  }
})();
